import { useRef } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { useTranslation } from '@/hooks/useTranslation';
import type { DichotomousConflict } from '../../dichotomous/dichotomousSearch';
import type { ConflictChoice } from '../../dichotomous/dichotomousPlan';
import type { ToolImage, ToolState } from '../../types/LabelingTypes';
import { formatCapturedAt } from '../../utils/formatCapturedAt';

const STATE_LABEL_KEY: Record<ToolState, string> = {
  ok: 'labeling.state.ok',
  worn: 'labeling.state.worn',
};

interface DetailRowProps {
  label: string;
  value: string;
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

interface ConflictDialogProps {
  open: boolean;
  conflict: DichotomousConflict;
  /** Image at `conflict.index` — resolved by the caller, only displayed here. */
  image: ToolImage;
  /** Position of `image` in the chronological sequence (`index` is zero-based). */
  position: { index: number; total: number };
  onResolve(choice: ConflictChoice): void;
}

/**
 * Blocking dialog shown when a verification answer contradicts the zone the search predicted.
 * It is deliberately non-dismissible: Escape is swallowed, there is no cancel affordance, and
 * outside interaction is already blocked by Radix's `AlertDialog` primitive itself. The only
 * ways out are the two explicit resolutions.
 *
 * Visibility is fully controlled by `open` — the parent decides when the dialog goes away, so
 * no `onOpenChange` is wired and the Radix actions' own auto-close is irrelevant here.
 */
export function ConflictDialog({
  open,
  conflict,
  image,
  position,
  onResolve,
}: ConflictDialogProps) {
  const { t } = useTranslation();
  const rerunRef = useRef<HTMLButtonElement>(null);

  const expectedLabel = t(STATE_LABEL_KEY[conflict.expected]);
  const givenLabel = t(STATE_LABEL_KEY[conflict.given]);
  const positionLabel = t('labeling.dichotomous.conflict.position', {
    index: position.index + 1,
    total: position.total,
  });

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        // Radix already hardcodes `onPointerDownOutside`/`onInteractOutside` to preventDefault
        // for alert dialogs, so Escape is the only dismissal path left to close off.
        onEscapeKeyDown={(event) => event.preventDefault()}
        // Radix focuses the cancel button on open; with no cancel button, focus would otherwise
        // stay outside the dialog and defeat the focus trap.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          rerunRef.current?.focus({ preventScroll: true });
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{t('labeling.dichotomous.conflict.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('labeling.dichotomous.conflict.body', {
              position: positionLabel,
              expected: expectedLabel,
              given: givenLabel,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <Card tier="muted" padding="none" className="flex flex-col gap-2 px-4 py-3">
          <DetailRow label={t('labeling.dichotomous.conflict.expected')} value={expectedLabel} />
          <DetailRow label={t('labeling.dichotomous.conflict.given')} value={givenLabel} />
          <span className="text-xs text-muted-foreground">
            {t('labeling.viewer.capturedAt', { datetime: formatCapturedAt(image.capturedAt) })}
          </span>
        </Card>

        <Callout size="inline" tone="warning">
          {t('labeling.dichotomous.conflict.overwriteWarning')}
        </Callout>

        <AlertDialogFooter>
          <AlertDialogAction ref={rerunRef} variant="default" onClick={() => onResolve('rerun')}>
            {t('labeling.dichotomous.conflict.actionRerun')}
          </AlertDialogAction>
          <AlertDialogAction variant="outline" onClick={() => onResolve('keep')}>
            {t('labeling.dichotomous.conflict.actionKeep')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
